import { ModalForm, TControlledFormComponentProps, TControlledFormProps } from '@chocolate-soup-inc/cs-frontend-components';
import { joiResolver } from '@hookform/resolvers/joi';
import Joi from 'joi';
import { useCallback, useMemo } from 'react';

import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { serializeError } from 'serialize-error';
import { TShipmentType } from '../../../entities/shipments/queries';
import { TCarriers, useSetShipmentAsShippedMutation, useUpdateShipmentMutation } from '../../../generated/graphql';
import styles from '../SetAsReadyToShipModal/SetAsReadyToShipModal.module.scss';

export type TSetAsReadyToShipModalProps = {
  shipment?: TShipmentType;
  onCancel: () => void;
  onSuccess: () => void;
};

export const ShipManuallyModal = (props: TSetAsReadyToShipModalProps) => {
  const { shipment, onCancel, onSuccess } = props;

  const formFields = useMemo(() => {
    return [
      {
        type: 'select',
        label: 'Carrier',
        name: 'carrierShipModal',
        inputOptions: {
          type: 'string',

          options: [
            { label: 'Canada Post', value: TCarriers.CanadaPost },
            { label: 'Purolator CA', value: TCarriers.PurolatorCa },
            { label: 'DHL Express CA', value: TCarriers.DhlExpressCanada },
            { label: 'UPS', value: TCarriers.Ups }
          ]
        }
      },
      {
        type: 'textField',
        label: 'Tracking Number',
        name: 'trackingNumberShipModal',
        id: 'trackingNumberShipModal',
        inputOptions: {
          type: 'string'
        }
      },
      {
        type: 'datePicker',
        label: 'Ship Date',
        name: 'actualShippingDateShipModal',
        inputOptions: {
          type: 'date',
          className: styles.numberInput
        }
      },
      {
        type: 'textField',
        label: 'Ship Cost',
        name: 'costShipModal',
        inputOptions: {
          type: 'number'
        }
      }
    ];
  }, []);

  const schema = useMemo(() => {
    return Joi.object().unknown(false).keys({
      trackingNumberShipModal: Joi.string().required(),
      actualShippingDateShipModal: Joi.date().required(),
      carrierShipModal: Joi.string().required(),
      costShipModal: Joi.number().required()
    });
  }, []);

  const [updateShipment] = useUpdateShipmentMutation();
  const [setShipmentAsShipped] = useSetShipmentAsShippedMutation();

  const onSubmit = useCallback<TControlledFormProps<any>['onValidSubmit']>(
    async (data) => {
      const updateInput = {
        id: shipment?.id as string,
        companyId: shipment?.companyId as string,
        version: shipment?._version as number,
        input: {
          actualShippingDate: format(data.actualShippingDateShipModal, 'yyyy-MM-dd'),
          trackingNumber: data.trackingNumberShipModal,
          label: {
            carrier: data.carrierShipModal,
            cost: data.costShipModal,
            createdAt: new Date(data.actualShippingDateShipModal).toISOString()
          }
        }
      };

      try {
        const { data: updatedShipment } = await updateShipment({
          variables: updateInput
        });

        toast.success('Shipment details have been updated.');

        await setShipmentAsShipped({
          variables: {
            id: shipment?.id as string,
            companyId: shipment?.companyId as string,
            version: updatedShipment?.updateShipment?._version as number
          }
        });

        toast.success('Shipment status has been updated.');

        onSuccess();
      } catch (error) {
        const serializedError = serializeError(error);
        toast.error(`There was an error updating the shipment information: ${serializedError}`);
      }
    },
    [onSuccess, shipment, updateShipment, setShipmentAsShipped]
  );

  return (
    <ModalForm
      closeModal={onCancel}
      confirmLabel='Update'
      contentClassName={styles.setAsReadyToShipModal}
      controlledFormProps={{
        className: styles.form,
        fields: formFields as TControlledFormComponentProps[],
        formProps: {
          resolver: joiResolver(schema, {
            convert: true,
            abortEarly: false,
            stripUnknown: false
          })
        },
        onValidSubmit: onSubmit
      }}
      headline='Update Shipment Details'
      onCancelClick={onCancel}
      size='small'
      supportingText='Provide shipment details from ShipStation'
    />
  );
};
